import { Modal, ModalContent } from '@heroui/react';
import {
  ShoppingCart,
  Check,
  Shield,
  Lock,
  Loader2,
  X,
  Tag,
  PackageCheck,
} from 'lucide-react';
import { formatCurrency } from '../../../../utils/currency';

interface SelectedAddOn {
  id: string;
  name: string;
  price: number;
}

interface BookingCheckoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  addOns: SelectedAddOn[];
  isLoading?: boolean;
  onConfirm: () => void;
}

const C = {
  primary: 'hsl(var(--primary))',
  background: 'hsl(var(--background))',
  success: 'hsl(var(--success))',
  foreground: 'hsl(var(--foreground))',
  mutedFg: 'hsl(var(--muted-foreground))',
  border: 'hsl(var(--border))',
  card: 'hsl(var(--card))',
  primaryFg: 'hsl(var(--primary-foreground))',
};

// The package itself is covered by the customer's session credits — this
// modal only exists to confirm chargeable add-ons (if any), so the package
// line is always shown at 0.
const PACKAGE_PRICE = 0;

/**
 * Confirmation checkout for booking a credit/package session.
 *
 * Rendered for every credit-session booking so the customer reviews what
 * they're about to confirm before the API call fires. When the customer has
 * selected paid add-ons, this also shows the itemized charges and payment
 * method; when there are none, it degrades to a plain "Confirm Booking"
 * review with a $0 total.
 *
 * The modal is intentionally locked while `isLoading` is true: it must stay
 * open until the booking succeeds, and the parent closes it from the
 * mutation's onSuccess handler. Backdrop clicks, Escape, and the close button
 * are all disabled during the pending state so a user can't dismiss it mid-
 * flight.
 */
const BookingCheckoutModal = ({
  open,
  onOpenChange,
  addOns,
  isLoading = false,
  onConfirm,
}: BookingCheckoutModalProps) => {
  const hasAddOns = addOns.length > 0;
  const addonSubtotal = addOns.reduce((sum, a) => sum + (a.price || 0), 0);
  const total = PACKAGE_PRICE + addonSubtotal;

  return (
    <Modal
      isOpen={open}
      onOpenChange={onOpenChange}
      size="2xl"
      scrollBehavior="inside"
      hideCloseButton
      isDismissable={!isLoading}
      isKeyboardDismissDisabled={isLoading}
      backdrop="opaque"
      classNames={{
        wrapper:
          'fixed inset-0 items-center justify-center !overflow-hidden p-2 sm:p-3',
        backdrop: 'bg-black/80',
        base: 'max-w-[620px] w-[calc(100vw-1rem)] max-h-[min(92vh,700px)] !my-0 bg-transparent shadow-none border-0 overflow-hidden',
        body: 'p-0 overflow-hidden',
      }}
    >
      <ModalContent className="p-0 gap-0 bg-transparent shadow-none border-0 max-h-[min(92vh,700px)] overflow-hidden">
        {(onClose) => (
          <div
            className="relative rounded-2xl shadow-2xl max-h-[min(92vh,700px)] overflow-y-auto overscroll-contain"
            style={{ background: C.card, color: C.foreground }}
          >
            <button
              type="button"
              aria-label="Close"
              disabled={isLoading}
              onClick={() => {
                onClose();
                onOpenChange(false);
              }}
              className="absolute right-3 top-3 z-20 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none disabled:opacity-30"
              style={{ color: C.mutedFg }}
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative px-3 sm:px-5 lg:px-6 pt-4 pb-4">
              {/* Header */}
              <div className="flex items-start gap-2.5 mb-3 pr-7">
                <div
                  className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center"
                  style={{
                    background: `color-mix(in oklab, ${C.primary} 10%, transparent)`,
                  }}
                >
                  <ShoppingCart
                    className="h-4 w-4"
                    style={{ color: C.primary }}
                  />
                </div>
                <div>
                  <h2
                    className="text-base sm:text-lg font-bold tracking-tight leading-tight"
                    style={{ color: C.foreground }}
                  >
                    {hasAddOns ? 'Confirm Add-ons' : 'Confirm Booking'}
                  </h2>
                  <p
                    className="mt-0.5 text-[11px] sm:text-xs"
                    style={{ color: C.mutedFg }}
                  >
                    {hasAddOns
                      ? 'Review your selected add-ons before confirming this appointment'
                      : 'Review your appointment details before confirming'}
                  </p>
                </div>
              </div>

              <div
                className="rounded-xl border p-3 sm:p-3.5"
                style={{ borderColor: C.border, background: C.background }}
              >
                <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
                  <span
                    className="flex items-center gap-1.5"
                    style={{ color: C.foreground }}
                  >
                    <PackageCheck
                      className="h-3.5 w-3.5"
                      style={{ color: C.mutedFg }}
                    />
                    Package Price
                  </span>
                  <span
                    className="font-semibold"
                    style={{ color: C.foreground }}
                  >
                    {formatCurrency(PACKAGE_PRICE)}
                  </span>
                </div>

                {hasAddOns && (
                  <div
                    className="space-y-1.5 pt-1 border-t"
                    style={{ borderColor: C.border }}
                  >
                    {addOns.map((addon) => (
                      <div
                        key={addon.id}
                        className="flex items-center justify-between text-xs sm:text-sm py-1"
                      >
                        <span
                          className="flex items-center gap-1.5 min-w-0"
                          style={{ color: C.foreground }}
                        >
                          <Tag
                            className="h-3.5 w-3.5 shrink-0"
                            style={{ color: C.mutedFg }}
                          />
                          <span className="truncate">{addon.name}</span>
                        </span>
                        <span
                          className="font-semibold shrink-0"
                          style={{ color: C.foreground }}
                        >
                          {formatCurrency(addon.price)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div
                  className="my-2 border-t"
                  style={{ borderColor: C.border }}
                />
                <div className="flex items-center justify-between">
                  <span
                    className="text-sm font-bold"
                    style={{ color: C.primary }}
                  >
                    {hasAddOns ? 'Total Due' : 'Total Due'}
                  </span>
                  <span
                    className="text-base sm:text-lg font-bold"
                    style={{ color: C.primary }}
                  >
                    {formatCurrency(total)}
                  </span>
                </div>
              </div>

              <div
                className="mt-3 rounded-lg p-2.5 flex items-start gap-2 border"
                style={{
                  background: `color-mix(in oklab, ${C.success} 8%, ${C.background})`,
                  borderColor: `color-mix(in oklab, ${C.success} 25%, transparent)`,
                }}
              >
                <Check
                  className="h-4 w-4 shrink-0 mt-0.5"
                  style={{ color: C.success }}
                />
                <div
                  className="text-[11px] sm:text-xs leading-snug"
                  style={{ color: C.mutedFg }}
                >
                  This appointment uses 1 appointment from your package — no
                  charge for the package itself.
                </div>
              </div>

              {hasAddOns && (
                <div
                  className="mt-3 rounded-lg p-2.5 flex items-start gap-2 border"
                  style={{
                    background: `color-mix(in oklab, ${C.primary} 6%, ${C.background})`,
                    borderColor: `color-mix(in oklab, ${C.primary} 20%, transparent)`,
                  }}
                >
                  <Shield
                    className="h-4 w-4 shrink-0 mt-0.5"
                    style={{ color: C.primary }}
                  />
                  <div
                    className="text-[11px] sm:text-xs leading-snug"
                    style={{ color: C.mutedFg }}
                  >
                    You'll be asked to pay for add-ons after confirming — or
                    settle them directly with the provider.
                  </div>
                </div>
              )}

              <button
                type="button"
                disabled={isLoading}
                onClick={() => onConfirm()}
                className="mt-3 w-full h-10 sm:h-11 text-xs sm:text-sm font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-80 disabled:pointer-events-none transition-opacity shrink-0"
                style={{
                  background: 'var(--gradient-popular)',
                  color: C.primaryFg,
                }}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="h-3.5 w-3.5" />
                )}
                {isLoading ? 'Booking...' : 'Confirm Booking'}
              </button>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default BookingCheckoutModal;
